import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  IndianRupee, Plus, Search, Phone, MessageCircle, Loader2,
  CheckCircle2, AlertTriangle, Clock, TrendingUp, Users,
  Trash2, RefreshCw, ChevronDown, ChevronRight, History,
  CalendarClock,
} from 'lucide-react';
import PaymentFollowup from './PaymentFollowup';

const API = process.env.REACT_APP_BACKEND_URL;
const RS = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const STATUSES = [
  { id: 'pending', label: 'Pending', color: 'bg-slate-100 text-slate-700' },
  { id: 'reminder_sent', label: 'Reminder Sent', color: 'bg-amber-100 text-amber-700' },
  { id: 'promised', label: 'Promised', color: 'bg-blue-100 text-blue-700' },
  { id: 'partial_paid', label: 'Partial Paid', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'paid', label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'dispute', label: 'Dispute', color: 'bg-orange-100 text-orange-700' },
  { id: 'bad_debt', label: 'Bad Debt', color: 'bg-red-100 text-red-700' },
];
const TYPE_LABELS = {
  monthly_commitment: 'Monthly Commitment',
  franchise: 'Franchise',
  corporate: 'Corporate (Invoice)',
};

export default function Payments() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', entry_type: '', period: '', aging: '', q: '' });
  const [collectors, setCollectors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    customer_id: '', entry_type: 'monthly_commitment', amount: '', period: '',
    due_date: '', notes: '', subscriber_count: '', rate_per_subscriber: '',
    sales_invoice_id: '', assigned_to: '',
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOpts, setCustomerOpts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [logForm, setLogForm] = useState({ action: 'call', description: '', next_followup_date: '' });
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'cash', transaction_ref: '', paid_on: '', notes: '' });
  const [waMessage, setWaMessage] = useState('');
  const [showWa, setShowWa] = useState(null);
  const [showPay, setShowPay] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/payments/dashboard`, { headers });
      if (r.ok) setDashboard(await r.json());
    } catch { /* noop */ }
  }, []); // eslint-disable-line

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await fetch(`${API}/api/payments/entries?${params}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setEntries(d.entries || []);
      }
    } catch { toast.error('Failed to load entries'); }
    setLoading(false);
  }, [filters]); // eslint-disable-line

  const loadCollectors = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/payments/collectors`, { headers });
      if (r.ok) setCollectors((await r.json()).collectors || []);
    } catch { /* noop */ }
  }, []); // eslint-disable-line

  useEffect(() => { loadDashboard(); loadCollectors(); }, [loadDashboard, loadCollectors]);
  useEffect(() => { if (tab === 'entries') loadEntries(); }, [tab, loadEntries]);

  // Customer search-as-you-type for the create dialog
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!customerSearch.trim()) { setCustomerOpts([]); return; }
      try {
        const r = await fetch(`${API}/api/customers?search=${encodeURIComponent(customerSearch)}`, { headers });
        if (r.ok) {
          const d = await r.json();
          // /api/customers returns an array directly
          setCustomerOpts((Array.isArray(d) ? d : (d.customers || [])).slice(0, 10));
        }
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [customerSearch]); // eslint-disable-line

  const handleCreate = async () => {
    if (!createForm.customer_id) { toast.error('Pick a customer'); return; }
    if (!createForm.amount || !createForm.period || !createForm.due_date) {
      toast.error('Amount, Period and Due date are required'); return;
    }
    const payload = {
      ...createForm,
      amount: parseFloat(createForm.amount),
      subscriber_count: createForm.subscriber_count ? parseInt(createForm.subscriber_count, 10) : null,
      rate_per_subscriber: createForm.rate_per_subscriber ? parseFloat(createForm.rate_per_subscriber) : null,
    };
    try {
      const r = await fetch(`${API}/api/payments/entries`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success('Entry created');
        setShowCreate(false);
        setCreateForm({ customer_id: '', entry_type: 'monthly_commitment', amount: '', period: '',
          due_date: '', notes: '', subscriber_count: '', rate_per_subscriber: '',
          sales_invoice_id: '', assigned_to: '' });
        setCustomerSearch('');
        loadEntries(); loadDashboard();
      } else {
        toast.error(d.detail || 'Failed');
      }
    } catch { toast.error('Network error'); }
  };

  const handleStatusChange = async (entry_id, status) => {
    try {
      const r = await fetch(`${API}/api/payments/entries/${entry_id}`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (r.ok) { toast.success('Status updated'); loadEntries(); loadDashboard(); }
    } catch { toast.error('Network error'); }
  };

  const handleAddLog = async (entry_id) => {
    if (!logForm.description.trim()) { toast.error('Description required'); return; }
    try {
      const r = await fetch(`${API}/api/payments/entries/${entry_id}/log`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(logForm),
      });
      if (r.ok) {
        toast.success('Log added');
        setLogForm({ action: 'call', description: '', next_followup_date: '' });
        loadEntries();
      }
    } catch { toast.error('Network error'); }
  };

  const handleRecordPayment = async () => {
    if (!showPay || !payForm.amount) { toast.error('Amount required'); return; }
    try {
      const r = await fetch(`${API}/api/payments/entries/${showPay.entry_id}/payment`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payForm, amount: parseFloat(payForm.amount) }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Payment recorded — balance ${RS(d.entry.balance)}`);
        setShowPay(null);
        setPayForm({ amount: '', payment_mode: 'cash', transaction_ref: '', paid_on: '', notes: '' });
        loadEntries(); loadDashboard();
      } else { toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleSendWa = async () => {
    if (!showWa) return;
    try {
      const r = await fetch(`${API}/api/payments/entries/${showWa.entry_id}/whatsapp-reminder`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_message: waMessage || null }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Reminder sent to ${d.phone}`);
        setShowWa(null); setWaMessage('');
        loadEntries(); loadDashboard();
      } else { toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleDelete = async (entry_id) => {
    if (!window.confirm('Delete this payment entry?')) return;
    try {
      const r = await fetch(`${API}/api/payments/entries/${entry_id}`, { method: 'DELETE', headers });
      if (r.ok) { toast.success('Deleted'); loadEntries(); loadDashboard(); }
    } catch { toast.error('Network error'); }
  };

  const statusBadge = (s) => {
    const x = STATUSES.find(o => o.id === s) || { label: s, color: 'bg-slate-100 text-slate-600' };
    return <Badge className={`${x.color} text-xs`}>{x.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <IndianRupee className="h-6 w-6" /> Payment Followup
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage receivables, follow-ups & collections — Monthly Commitment / Franchise / Corporate
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadDashboard(); loadEntries(); }} data-testid="payments-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="payments-create-btn">
            <Plus className="h-4 w-4 mr-1" /> New Entry
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><TrendingUp className="h-4 w-4 mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="followup" data-testid="tab-followup"><CalendarClock className="h-4 w-4 mr-1" />Follow-up</TabsTrigger>
          <TabsTrigger value="entries" data-testid="tab-entries"><IndianRupee className="h-4 w-4 mr-1" />Entries</TabsTrigger>
          <TabsTrigger value="defaulters" data-testid="tab-defaulters"><AlertTriangle className="h-4 w-4 mr-1" />Defaulters</TabsTrigger>
        </TabsList>

        {/* FOLLOW-UP (recurring / non-recurring) */}
        <TabsContent value="followup" className="space-y-3">
          <PaymentFollowup />
        </TabsContent>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-4">
          {!dashboard ? (
            <div className="p-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-4">
                <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-red-100 text-xs">Total Outstanding</p>
                    <p className="text-2xl font-bold">{RS(dashboard.total_outstanding)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-emerald-100 text-xs">Collected (this month)</p>
                    <p className="text-2xl font-bold">{RS(dashboard.monthly_collection?.total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-slate-900">{dashboard.status_counts?.pending || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Promised</p>
                    <p className="text-2xl font-bold text-blue-600">{dashboard.status_counts?.promised || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Aging */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Aging Buckets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-5">
                    {['not_due', '0-30', '31-60', '61-90', '90+'].map(k => (
                      <div key={k} className="rounded-md border p-3">
                        <p className="text-xs text-slate-500 uppercase">{k === 'not_due' ? 'Not Due' : `${k} days`}</p>
                        <p className="text-lg font-bold mt-1">{RS(dashboard.buckets?.[k]?.amount)}</p>
                        <p className="text-xs text-slate-400">{dashboard.buckets?.[k]?.count || 0} entries</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Productivity */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Collector Productivity (this month)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(dashboard.monthly_collection?.by_collector || []).length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">No collections this month</div>
                  ) : (
                    <div className="divide-y">
                      {dashboard.monthly_collection.by_collector.map(c => (
                        <div key={c.collector} className="p-3 flex items-center justify-between text-sm">
                          <span className="font-medium">{c.collector}</span>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">{RS(c.amount)}</p>
                            <p className="text-xs text-slate-400">{c.count} payment{c.count > 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ENTRIES */}
        <TabsContent value="entries" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Search customer/phone/notes" value={filters.q}
                   onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                   className="max-w-xs" data-testid="payments-search-input" />
            <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-44" data-testid="payments-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.entry_type || 'all'} onValueChange={v => setFilters(f => ({ ...f, entry_type: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-44" data-testid="payments-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.aging || 'all'} onValueChange={v => setFilters(f => ({ ...f, aging: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-32" data-testid="payments-aging-filter"><SelectValue placeholder="Aging" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ages</SelectItem>
                <SelectItem value="0-30">0-30</SelectItem>
                <SelectItem value="31-60">31-60</SelectItem>
                <SelectItem value="61-90">61-90</SelectItem>
                <SelectItem value="90+">90+</SelectItem>
              </SelectContent>
            </Select>
            <Input type="month" value={filters.period}
                   onChange={e => setFilters(f => ({ ...f, period: e.target.value }))}
                   className="w-44" data-testid="payments-period-filter" />
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
              ) : entries.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <IndianRupee className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No payment entries</p>
                  <p className="text-xs mt-1">Add the first one to start tracking collections</p>
                </div>
              ) : (
                <div className="divide-y">
                  {entries.map(e => (
                    <div key={e.entry_id} data-testid={`payment-row-${e.entry_id}`}>
                      <div className="p-4 flex items-start gap-3 hover:bg-slate-50">
                        <button
                          onClick={() => setOpenId(openId === e.entry_id ? null : e.entry_id)}
                          className="mt-1 text-slate-400 hover:text-slate-700"
                          data-testid={`payment-expand-${e.entry_id}`}
                        >
                          {openId === e.entry_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-slate-900">{e.customer_name || e.customer_id}</p>
                            <Badge variant="outline" className="text-xs">{TYPE_LABELS[e.entry_type] || e.entry_type}</Badge>
                            {statusBadge(e.status)}
                            {e.days_overdue > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{e.days_overdue}d overdue</Badge>}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                            <span><Phone className="h-3 w-3 inline mr-0.5" />{e.contact_phone || '—'}</span>
                            <span>Period: <b>{e.period}</b></span>
                            <span>Due: {e.due_date}</span>
                            {e.entry_type === 'franchise' && e.subscriber_count ? <span>{e.subscriber_count} subs × {RS(e.rate_per_subscriber)}</span> : null}
                            {e.assigned_to && <span>Assignee: {e.assigned_to}</span>}
                            {e.reminders_count > 0 && <span>{e.reminders_count} reminder{e.reminders_count > 1 ? 's' : ''}</span>}
                          </div>
                          {e.notes && <p className="text-xs text-slate-500 mt-1 italic">"{e.notes}"</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{RS(e.amount)}</p>
                          <p className="text-xs text-slate-500">Paid: {RS(e.paid_amount)}</p>
                          <p className="text-xs font-semibold text-red-600">Bal: {RS(e.balance)}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          {e.balance > 0 && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                                    onClick={() => { setShowPay(e); setPayForm({ amount: e.balance, payment_mode: 'cash', transaction_ref: '', paid_on: '', notes: '' }); }}
                                    data-testid={`payment-record-${e.entry_id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Record
                            </Button>
                          )}
                          {e.balance > 0 && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    onClick={() => { setShowWa(e); setWaMessage(''); }}
                                    data-testid={`payment-wa-${e.entry_id}`}>
                              <MessageCircle className="h-3 w-3 mr-1" /> WA
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50"
                                  onClick={() => handleDelete(e.entry_id)}
                                  data-testid={`payment-delete-${e.entry_id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {openId === e.entry_id && (
                        <div className="bg-slate-50 p-4 space-y-3 border-t">
                          {/* Status change */}
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Status:</Label>
                            <Select value={e.status} onValueChange={v => handleStatusChange(e.entry_id, v)}>
                              <SelectTrigger className="w-44 h-8" data-testid={`payment-status-select-${e.entry_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Add log */}
                          <div className="grid gap-2 md:grid-cols-[120px_1fr_160px_auto]">
                            <Select value={logForm.action} onValueChange={v => setLogForm(f => ({ ...f, action: v }))}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['call', 'visit', 'whatsapp', 'email', 'note'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input placeholder="Follow-up note (e.g., promised to pay by Friday)"
                                   value={logForm.description}
                                   onChange={ev => setLogForm(f => ({ ...f, description: ev.target.value }))}
                                   data-testid={`payment-log-desc-${e.entry_id}`} />
                            <Input type="date" value={logForm.next_followup_date}
                                   onChange={ev => setLogForm(f => ({ ...f, next_followup_date: ev.target.value }))}
                                   placeholder="Next followup" />
                            <Button size="sm" onClick={() => handleAddLog(e.entry_id)} data-testid={`payment-log-save-${e.entry_id}`}>Add Log</Button>
                          </div>
                          {/* History */}
                          {(e.history || []).length > 0 && (
                            <div className="border-t pt-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                                <History className="h-3 w-3" /> History ({e.history.length})
                              </p>
                              <div className="space-y-1 max-h-56 overflow-y-auto">
                                {[...e.history].reverse().map(h => (
                                  <div key={h.log_id || h.timestamp} className="text-xs flex items-start gap-2 py-1">
                                    <Badge variant="outline" className="text-[10px] uppercase shrink-0">{h.action}</Badge>
                                    <div className="flex-1">
                                      <p className="text-slate-700">{h.description}</p>
                                      <p className="text-slate-400">{new Date(h.timestamp).toLocaleString('en-IN')} • {h.by_user || 'system'}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DEFAULTERS */}
        <TabsContent value="defaulters">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /> Top Defaulters</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(dashboard?.top_defaulters || []).length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm">No outstanding defaulters</div>
              ) : (
                <div className="divide-y">
                  {dashboard.top_defaulters.map(d => (
                    <div key={d.customer_id} className="p-4 flex items-center justify-between" data-testid={`defaulter-${d.customer_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{d.customer_name}</p>
                        <p className="text-xs text-slate-500">
                          <Phone className="h-3 w-3 inline mr-0.5" />{d.contact_phone || '—'}
                          {d.customer_type ? ` • ${d.customer_type}` : ''}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {d.entries} entr{d.entries > 1 ? 'ies' : 'y'} • Oldest due: {d.oldest_due_date} ({d.days_overdue}d overdue)
                        </p>
                      </div>
                      <p className="font-bold text-red-600">{RS(d.outstanding)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="payment-create-dialog">
          <DialogHeader>
            <DialogTitle>New Payment Entry</DialogTitle>
            <DialogDescription>Track a receivable for a customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Customer *</Label>
              <Input placeholder="Search by name / phone / code..."
                     value={customerSearch}
                     onChange={e => setCustomerSearch(e.target.value)}
                     data-testid="payment-customer-search" />
              {customerOpts.length > 0 && (
                <div className="border rounded-md mt-1 max-h-40 overflow-y-auto">
                  {customerOpts.map(c => (
                    <button key={c.customer_id} type="button"
                            onClick={() => { setCreateForm(f => ({ ...f, customer_id: c.customer_id })); setCustomerSearch(c.company_name); setCustomerOpts([]); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                      <p className="font-medium">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.contact_phone} • {c.customer_type}</p>
                    </button>
                  ))}
                </div>
              )}
              {createForm.customer_id && <p className="text-xs text-emerald-600 mt-1">✓ Customer selected</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={createForm.entry_type} onValueChange={v => setCreateForm(f => ({ ...f, entry_type: v }))}>
                  <SelectTrigger data-testid="payment-create-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period (YYYY-MM) *</Label>
                <Input type="month" value={createForm.period}
                       onChange={e => setCreateForm(f => ({ ...f, period: e.target.value }))}
                       data-testid="payment-create-period" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs.) *</Label>
                <Input type="number" step="0.01" value={createForm.amount}
                       onChange={e => setCreateForm(f => ({ ...f, amount: e.target.value }))}
                       data-testid="payment-create-amount" />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input type="date" value={createForm.due_date}
                       onChange={e => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
                       data-testid="payment-create-due" />
              </div>
            </div>
            {createForm.entry_type === 'franchise' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Subscriber Count</Label>
                  <Input type="number" value={createForm.subscriber_count}
                         onChange={e => setCreateForm(f => ({ ...f, subscriber_count: e.target.value }))} />
                </div>
                <div>
                  <Label>Rate / Subscriber</Label>
                  <Input type="number" step="0.01" value={createForm.rate_per_subscriber}
                         onChange={e => setCreateForm(f => ({ ...f, rate_per_subscriber: e.target.value }))} />
                </div>
              </div>
            )}
            {createForm.entry_type === 'corporate' && (
              <div>
                <Label>Sales Invoice ID (optional)</Label>
                <Input value={createForm.sales_invoice_id}
                       onChange={e => setCreateForm(f => ({ ...f, sales_invoice_id: e.target.value }))} />
              </div>
            )}
            <div>
              <Label>Assignee (Collector)</Label>
              <Select value={createForm.assigned_to || 'unassigned'} onValueChange={v => setCreateForm(f => ({ ...f, assigned_to: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Pick a collector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">— Unassigned —</SelectItem>
                  {collectors.map(c => <SelectItem key={c.email} value={c.email}>{c.full_name || c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={createForm.notes}
                        onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="payment-create-save"><Plus className="h-4 w-4 mr-1" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!showPay} onOpenChange={v => { if (!v) setShowPay(null); }}>
        <DialogContent data-testid="payment-record-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{showPay?.customer_name} • Balance {RS(showPay?.balance)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount Received *</Label>
                <Input type="number" step="0.01" value={payForm.amount}
                       onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                       data-testid="payment-record-amount" />
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={payForm.payment_mode} onValueChange={v => setPayForm(f => ({ ...f, payment_mode: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['cash', 'bank_transfer', 'upi', 'cheque', 'other'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Transaction Ref / UTR</Label>
                <Input value={payForm.transaction_ref}
                       onChange={e => setPayForm(f => ({ ...f, transaction_ref: e.target.value }))} />
              </div>
              <div>
                <Label>Paid On</Label>
                <Input type="date" value={payForm.paid_on}
                       onChange={e => setPayForm(f => ({ ...f, paid_on: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={payForm.notes}
                        onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(null)}>Cancel</Button>
            <Button onClick={handleRecordPayment} data-testid="payment-record-save"><CheckCircle2 className="h-4 w-4 mr-1" /> Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Reminder Dialog */}
      <Dialog open={!!showWa} onOpenChange={v => { if (!v) setShowWa(null); }}>
        <DialogContent data-testid="payment-wa-dialog">
          <DialogHeader>
            <DialogTitle>Send WhatsApp Reminder</DialogTitle>
            <DialogDescription>{showWa?.customer_name} • {showWa?.contact_phone}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 rounded p-3 text-sm">
              <p className="text-slate-500">Default template (used if you leave the box empty):</p>
              <p className="text-slate-700 mt-1 whitespace-pre-wrap">
                {`Dear ${showWa?.contact_name || showWa?.customer_name || 'Sir/Madam'},

This is a friendly reminder that an amount of Rs. ${(showWa?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} is pending for ${showWa?.period}.
Due date: ${showWa?.due_date}.

Kindly arrange the payment at the earliest.

— ANT Broadband`}
              </p>
            </div>
            <div>
              <Label>Or write a custom message</Label>
              <Textarea rows={4} value={waMessage} onChange={e => setWaMessage(e.target.value)}
                        placeholder="(optional) Custom WhatsApp message..."
                        data-testid="payment-wa-custom" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWa(null)}>Cancel</Button>
            <Button onClick={handleSendWa} data-testid="payment-wa-send">
              <MessageCircle className="h-4 w-4 mr-1" /> Send Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
